import { Box } from "@chakra-ui/react"
import { useRecoilValue } from "recoil"
import { searchNavigationParams } from "../../../../../common/constants/constants"
import { breadcrumbs, filterConstants, tabs } from "../../../../../common/constants/search-constants"
import { useQueryParam } from "../../../../../common/lib/QueryParamContext"
import { searchServiceLocaleState } from "../../../../../common/store/settingsStore"
import { Tab } from "../../../../../common/types/global"
import { FhResponseData } from "../../../../../common/types/search"
import { isFilterToBeRemoved } from "../../../../../common/utils/search-utils"
import Filter from "./Filter/Filter"
import MobileFilter from "./Filter/MobileFilter"

type Props = {
  globalFilters: FhResponseData
  allSearchFilters: FhResponseData
  selectedTab: Tab
  breadcrumbFilters: FhResponseData
  currentTabCount: number
  showFiltersNoResultsView: boolean
  /* Optional props from the mobile overlay window component */
  isOverlayFilters?: boolean
  closeFiltersOverlay?: () => void
  rid: string
}

const AllFilters = ({
  globalFilters,
  allSearchFilters,
  selectedTab,
  breadcrumbFilters,
  currentTabCount,
  showFiltersNoResultsView,
  /* These following two props are passed only from the mobile filters component */
  isOverlayFilters,
  closeFiltersOverlay,
  rid,
}: Props) => {
  const { queryParam } = useQueryParam()
  const searchServiceLocale = useRecoilValue(searchServiceLocaleState)

  /* If on Novelties navigation page, remove the New filter */
  if (queryParam.name === searchNavigationParams.NEW) {
    allSearchFilters = allSearchFilters?.filter(
      (filter: FhResponseData) => filter.on !== `${breadcrumbs.NEW}_${searchServiceLocale.toLowerCase()}`
    )
  }

  /* Determine which local filters to get from the response based on selected tab */
  const activeLocalFiltersCode = selectedTab === tabs.PRO ? filterConstants.PRODUCTS : filterConstants.SPARE_PART_TABLES
  const searchFilters = allSearchFilters?.filter((filter: FhResponseData) => {
    return filter?.custom_fields?.custom_field?.find((field: FhResponseData) => field.value === activeLocalFiltersCode)
  })

  /* Filter out filters and filters options based on business logic */
  const renderedGlobalFilters = globalFilters?.filter(
    (filterData: FhResponseData) => !isFilterToBeRemoved(filterData, currentTabCount, showFiltersNoResultsView)
  )
  const renderedSearchFilters = searchFilters?.filter(
    (filterData: FhResponseData) => !isFilterToBeRemoved(filterData, currentTabCount, showFiltersNoResultsView)
  )

  return (
    <>
      {/* Global filters */}
      {renderedGlobalFilters?.map((filterData: FhResponseData) => {
        return isOverlayFilters ? (
          <MobileFilter
            filterData={filterData}
            breadcrumbFilters={breadcrumbFilters}
            selectedTab={selectedTab}
            showFiltersNoResultsView={showFiltersNoResultsView}
            isOverlayFilters={isOverlayFilters}
            closeFiltersOverlay={closeFiltersOverlay}
            key={filterData.facetid}
            rid={rid}
          />
        ) : (
          <Filter
            filterData={filterData}
            breadcrumbFilters={breadcrumbFilters}
            showFiltersNoResultsView={showFiltersNoResultsView}
            key={filterData.facetid}
            rid={rid}
          />
        )
      })}
      {/* Dash separator */}
      {!!renderedGlobalFilters?.length && !!renderedSearchFilters?.length && (
        <Box
          alignSelf="center"
          my={isOverlayFilters ? "4" : "0"}
          w={isOverlayFilters ? "100%" : "6"}
          h="px"
          background="gray.300"
        />
      )}
      {/* Local tab specific filters */}
      {renderedSearchFilters?.map((filterData: FhResponseData) => {
        return isOverlayFilters ? (
          <MobileFilter
            filterData={filterData}
            breadcrumbFilters={breadcrumbFilters}
            selectedTab={selectedTab}
            showFiltersNoResultsView={showFiltersNoResultsView}
            isOverlayFilters={isOverlayFilters}
            closeFiltersOverlay={closeFiltersOverlay}
            key={filterData.facetid}
            rid={rid}
          />
        ) : (
          <Filter
            filterData={filterData}
            breadcrumbFilters={breadcrumbFilters}
            showFiltersNoResultsView={showFiltersNoResultsView}
            key={filterData.facetid}
            rid={rid}
          />
        )
      })}
    </>
  )
}

export default AllFilters
